import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '@/utils/cn';

type ToggleProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: ReactNode;
  /** Label-Position: rechts (default) oder links */
  labelPosition?: 'left' | 'right';
};

/**
 * Sizilianer-Toggle (Switch).
 * Gold-Hintergrund im Checked-State, smooth Slide.
 *
 * @example
 *   <Toggle label="Newsletter abonnieren" defaultChecked />
 */
export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { label, labelPosition = 'right', className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const switchEl = (
    <span className="relative inline-block shrink-0">
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        role="switch"
        className="peer sr-only"
        {...props}
      />
      <span
        className={cn(
          'block w-10 h-6 rounded-full bg-rule transition-colors',
          'peer-checked:bg-gold',
          'peer-focus-visible:outline-2 peer-focus-visible:outline-gold peer-focus-visible:outline-offset-2',
        )}
      />
      <span
        className={cn(
          'absolute top-0.5 left-0.5 block size-5 rounded-full bg-paper',
          'shadow-sm transition-transform',
          'peer-checked:translate-x-4',
        )}
        aria-hidden="true"
      />
    </span>
  );

  if (!label) {
    return (
      <label
        htmlFor={inputId}
        className={cn(
          'inline-flex cursor-pointer',
          props.disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        {switchEl}
      </label>
    );
  }

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'inline-flex items-center gap-2.5 cursor-pointer',
        props.disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      {labelPosition === 'left' && (
        <span className="font-sans text-sm text-ink-2 select-none">{label}</span>
      )}
      {switchEl}
      {labelPosition === 'right' && (
        <span className="font-sans text-sm text-ink-2 select-none">{label}</span>
      )}
    </label>
  );
});
